import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import SEOHead from '../components/SEOHead';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../translations/translations';
import './Products.css';

const PAGE_SIZE_OPTIONS = [12, 15, 24, 36, 50];

const LOCALE_MAP = { en: 'en-GB', nl: 'nl-NL', de: 'de-DE', fr: 'fr-FR' };

function formatMoney(amountStr, currency, locale) {
  if (amountStr == null || amountStr === '') return null;
  const n = parseFloat(String(amountStr).replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  const cur = currency && String(currency).trim() ? String(currency).trim().toUpperCase() : 'EUR';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: cur }).format(n);
  } catch {
    return `€ ${n.toFixed(2).replace('.', ',')}`;
  }
}

/**
 * Magento-catalogus — grid/list zoals categoriepagina, in SDeal-stijl.
 */
const ProductCatalog = () => {
  const { currentLanguage } = useLanguage();
  const { getToken, isLoaded: authLoaded } = useAuth();
  const locale = LOCALE_MAP[currentLanguage] || 'en-GB';

  const [searchInput, setSearchInput] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogPage, setCatalogPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [catalogSort, setCatalogSort] = useState('name_asc');
  const [viewMode, setViewMode] = useState('grid');
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);

  const loadCatalog = useCallback(
    async (page, q, limit, sort) => {
      if (!authLoaded) return;
      setCatalogLoading(true);
      setCatalogError(null);
      try {
        const token = await getToken();
        if (!token) {
          setCatalogError(getTranslation(currentLanguage, 'catalogUnauthorized'));
          setCatalogItems([]);
          setCatalogTotal(0);
          return;
        }
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          sort: sort || 'name_asc',
        });
        const trimmed = q.trim();
        if (trimmed.length >= 2) {
          params.set('q', trimmed);
        }
        const res = await fetch(`/api/products/search?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          setCatalogError(getTranslation(currentLanguage, 'catalogUnauthorized'));
          setCatalogItems([]);
          setCatalogTotal(0);
          return;
        }
        if (!res.ok) {
          throw new Error('request failed');
        }
        const data = await res.json();
        setCatalogItems(Array.isArray(data.items) ? data.items : []);
        setCatalogTotal(typeof data.total === 'number' ? data.total : 0);
      } catch {
        setCatalogError(getTranslation(currentLanguage, 'catalogLoadError'));
        setCatalogItems([]);
        setCatalogTotal(0);
      } finally {
        setCatalogLoading(false);
      }
    },
    [currentLanguage, authLoaded, getToken]
  );

  useEffect(() => {
    if (!authLoaded) return;
    loadCatalog(catalogPage, appliedQuery, pageSize, catalogSort);
  }, [catalogPage, appliedQuery, pageSize, catalogSort, loadCatalog, authLoaded]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedQuery(searchInput.trim());
    setCatalogPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(catalogTotal / pageSize));
  const rangeStart = catalogTotal === 0 ? 0 : (catalogPage - 1) * pageSize + 1;
  const rangeEnd = catalogTotal === 0 ? 0 : Math.min(catalogPage * pageSize, catalogTotal);

  const onPageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCatalogPage(1);
  };

  const onSortChange = (e) => {
    setCatalogSort(e.target.value);
    setCatalogPage(1);
  };

  const goPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCatalogPage(p);
  };

  const visiblePageButtons = () => {
    const maxBtns = 5;
    if (totalPages <= maxBtns) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = new Set([1, 2, 3, totalPages, catalogPage, catalogPage - 1, catalogPage + 1]);
    return [...pages].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  };

  const displayPrice = (item) => {
    const cur = item.currency || 'EUR';
    if (item.specialPrice) {
      const sale = formatMoney(item.specialPrice, cur, locale);
      const was = formatMoney(item.price, cur, locale);
      return { main: sale, was };
    }
    const p = formatMoney(item.price, cur, locale);
    return { main: p, was: null };
  };

  const renderProductCard = (item) => {
    const { main: priceMain, was: priceWas } = displayPrice(item);
    const inner = (
      <>
        <div className="catalog-grid-image-wrap">
          {item.thumbnailUrl ? (
            <img
              src={item.thumbnailUrl}
              alt=""
              className="catalog-grid-image"
              loading="lazy"
            />
          ) : (
            <div className="catalog-grid-image catalog-grid-image--placeholder" aria-hidden />
          )}
        </div>
        <h2 className="catalog-grid-title">{item.name}</h2>
        {priceMain ? (
          <div className="catalog-grid-price-row">
            <span className="catalog-grid-price-pill">{priceMain}</span>
            {priceWas ? <span className="catalog-grid-price-was">{priceWas}</span> : null}
          </div>
        ) : null}
      </>
    );

    if (item.catalogUrl) {
      return (
        <a
          href={item.catalogUrl}
          className="catalog-grid-card"
          target="_blank"
          rel="noopener noreferrer"
        >
          {inner}
        </a>
      );
    }
    return <div className="catalog-grid-card catalog-grid-card--static">{inner}</div>;
  };

  const renderProductListRow = (item) => {
    const { main: priceMain, was: priceWas } = displayPrice(item);
    return (
      <li key={item.id} className="catalog-list-row">
        <div className="catalog-list-image-wrap">
          {item.thumbnailUrl ? (
            <img src={item.thumbnailUrl} alt="" className="catalog-list-image" loading="lazy" />
          ) : (
            <div className="catalog-list-image catalog-list-image--placeholder" aria-hidden />
          )}
        </div>
        <div className="catalog-list-body">
          <h2 className="catalog-list-title">{item.name}</h2>
          <div className="catalog-list-meta">SKU {item.sku}</div>
        </div>
        <div className="catalog-list-actions">
          {priceMain ? (
            <span className="catalog-grid-price-pill">{priceMain}</span>
          ) : null}
          {priceWas ? <span className="catalog-grid-price-was">{priceWas}</span> : null}
          {item.catalogUrl ? (
            <a
              href={item.catalogUrl}
              className="catalog-result-button catalog-list-cta"
              target="_blank"
              rel="noopener noreferrer"
            >
              {getTranslation(currentLanguage, 'catalogViewOnShop')}
            </a>
          ) : null}
        </div>
      </li>
    );
  };

  const pageNumbers = visiblePageButtons();

  return (
    <>
      <SEOHead
        title={`${getTranslation(currentLanguage, 'catalogSearchTitle')} - SDeal`}
        description={getTranslation(currentLanguage, 'catalogHint')}
        keywords="SDeal, catalog, shop"
      />
      <div className="catalog-page w3-content w3-padding" style={{ maxWidth: '1564px', marginTop: '64px' }}>
        <div className="w3-container w3-padding-16">
          <h1 id="catalog-heading" className="catalog-page-title">
            {getTranslation(currentLanguage, 'catalogSearchTitle')}
          </h1>

          <form className="catalog-search-form catalog-search-form--page" onSubmit={onSearchSubmit}>
            <input
              type="search"
              className="catalog-search-input"
              placeholder={getTranslation(currentLanguage, 'catalogSearchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={getTranslation(currentLanguage, 'catalogSearchPlaceholder')}
            />
            <button type="submit" className="catalog-search-button catalog-search-button--sdeal" disabled={catalogLoading}>
              {getTranslation(currentLanguage, 'catalogSearchButton')}
            </button>
          </form>

          <section className="catalog-section catalog-section--standalone" aria-labelledby="catalog-heading">
            {catalogError && <p className="catalog-error" role="alert">{catalogError}</p>}
            {catalogLoading && catalogTotal === 0 && !catalogError && (
              <p className="catalog-loading">{getTranslation(currentLanguage, 'catalogLoading')}</p>
            )}

            {!catalogLoading && !catalogError && catalogTotal === 0 && (
              <p className="catalog-empty">{getTranslation(currentLanguage, 'catalogEmpty')}</p>
            )}

            {!catalogError && catalogTotal > 0 && (
              <>
                <div className="catalog-toolbar">
                  <div className="catalog-toolbar-left">
                    <div className="catalog-view-toggle" role="group" aria-label={getTranslation(currentLanguage, 'catalogViewMode')}>
                      <button
                        type="button"
                        className={`catalog-view-btn ${viewMode === 'grid' ? 'is-active' : ''}`}
                        onClick={() => setViewMode('grid')}
                        aria-pressed={viewMode === 'grid'}
                        title={getTranslation(currentLanguage, 'catalogViewGrid')}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                          <path d="M4 4h7v7H4V4zm9 0h7v7h-7V4zM4 13h7v7H4v-7zm9 0h7v7h-7v-7z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className={`catalog-view-btn ${viewMode === 'list' ? 'is-active' : ''}`}
                        onClick={() => setViewMode('list')}
                        aria-pressed={viewMode === 'list'}
                        title={getTranslation(currentLanguage, 'catalogViewList')}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                          <path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z" />
                        </svg>
                      </button>
                    </div>
                    <span className="catalog-range">
                      {rangeStart}-{rangeEnd} {getTranslation(currentLanguage, 'catalogOf')} {catalogTotal.toLocaleString(locale)}
                    </span>
                  </div>

                  <div className="catalog-toolbar-center">
                    <nav className="catalog-pagination-bar" aria-label={getTranslation(currentLanguage, 'catalogPagination')}>
                      <button
                        type="button"
                        className="catalog-pg-btn"
                        disabled={catalogPage <= 1}
                        onClick={() => goPage(catalogPage - 1)}
                        aria-label={getTranslation(currentLanguage, 'catalogPrevPage')}
                      >
                        ‹
                      </button>
                      {pageNumbers.map((p, idx) => (
                        <React.Fragment key={p}>
                          {idx > 0 && pageNumbers[idx - 1] !== p - 1 && (
                            <span className="catalog-pg-gap" aria-hidden>
                              …
                            </span>
                          )}
                          <button
                            type="button"
                            className={`catalog-pg-btn ${p === catalogPage ? 'is-active' : ''}`}
                            onClick={() => goPage(p)}
                            aria-current={p === catalogPage ? 'page' : undefined}
                          >
                            {p}
                          </button>
                        </React.Fragment>
                      ))}
                      <button
                        type="button"
                        className="catalog-pg-btn"
                        disabled={catalogPage >= totalPages}
                        onClick={() => goPage(catalogPage + 1)}
                        aria-label={getTranslation(currentLanguage, 'catalogNextPage')}
                      >
                        ›
                      </button>
                    </nav>
                  </div>

                  <div className="catalog-toolbar-right">
                    <label className="catalog-toolbar-label">
                      <span>{getTranslation(currentLanguage, 'catalogShow')}</span>
                      <select className="catalog-toolbar-select" value={pageSize} onChange={onPageSizeChange}>
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="catalog-toolbar-label">
                      <span>{getTranslation(currentLanguage, 'catalogSortBy')}</span>
                      <select className="catalog-toolbar-select" value={catalogSort} onChange={onSortChange}>
                        <option value="name_asc">{getTranslation(currentLanguage, 'catalogSortPosition')}</option>
                        <option value="name_desc">{getTranslation(currentLanguage, 'catalogSortNameDesc')}</option>
                        <option value="price_asc">{getTranslation(currentLanguage, 'catalogSortPriceAsc')}</option>
                        <option value="price_desc">{getTranslation(currentLanguage, 'catalogSortPriceDesc')}</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div
                  className={
                    catalogLoading ? 'catalog-results-wrap catalog-results-wrap--loading' : 'catalog-results-wrap'
                  }
                  aria-busy={catalogLoading}
                >
                  {viewMode === 'grid' ? (
                    <ul className="catalog-grid">
                      {catalogItems.map((item) => (
                        <li key={item.id} className="catalog-grid-cell">
                          {renderProductCard(item)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul className="catalog-list">{catalogItems.map((item) => renderProductListRow(item))}</ul>
                  )}
                  {catalogLoading ? (
                    <p className="catalog-loading catalog-loading--overlay">{getTranslation(currentLanguage, 'catalogLoading')}</p>
                  ) : null}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

export default ProductCatalog;
